import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
];

const LanguageSelector = ({ variant = 'default', className = '' }) => {
  const { i18n } = useTranslation();

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  if (variant === 'compact') {
    return (
      <Select value={i18n.language} onValueChange={handleLanguageChange}>
        <SelectTrigger 
          className={`w-auto h-9 px-2 bg-transparent border-0 hover:bg-white/10 ${className}`}
          data-testid="language-selector"
        >
          <div className="flex items-center gap-1">
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium">{currentLang.flag}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="bg-white min-w-[150px]">
          {languages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <div className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (variant === 'minimal') {
    return (
      <Select value={i18n.language} onValueChange={handleLanguageChange}>
        <SelectTrigger 
          className={`w-auto h-8 px-2 bg-white/10 border-white/20 hover:bg-white/20 text-white ${className}`}
          data-testid="language-selector"
        >
          <div className="flex items-center gap-1.5">
            <span className="text-base">{currentLang.flag}</span>
            <span className="text-xs uppercase font-medium">{currentLang.code}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="bg-white min-w-[150px]">
          {languages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <div className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span className="text-gray-900">{lang.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Default variant
  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger 
        className={`w-[160px] h-10 bg-white border-gray-300 ${className}`}
        data-testid="language-selector"
      >
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-gray-500" />
          <SelectValue>
            <div className="flex items-center gap-2">
              <span>{currentLang.flag}</span>
              <span>{currentLang.name}</span>
            </div>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-white">
        {languages.map((lang) => (
          <SelectItem key={lang.code} value={lang.code}>
            <div className="flex items-center gap-2">
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSelector;
